/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 **/
import { useState } from 'react'
import { Icon, Text, TextField, View, Loader } from '@az/starc-ui'
import { Search, Close } from '@az/starc-ui-icons'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import ItemLookupDataService from '../../services/ItemLookupDataService'
import { Item } from '../../types'
import style from './ItemSearch.module.css'

interface ItemsSearchProps {
  storeNumber: string
  setItems: (search: string, items: Item[]) => void
}

let source = axios.CancelToken.source()

const ItemSearch: React.FC<ItemsSearchProps> = props => {
  const { storeNumber, setItems } = props
  const [itemSearchValue, setSearchValue] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const { t } = useTranslation()

  async function fetchItems(newSearch: string) {
    newSearch = newSearch.trim()
    setItems('', [])
    if (newSearch !== '') {
      setIsLoading(true)
      try {
        const response = await ItemLookupDataService.findAllByTerm(storeNumber, newSearch, {
          cancelToken: source.token
        })
        setItems(newSearch, response.data)
      } catch (e: any) {
        if (axios.isCancel(e)) {
          // eslint-disable-next-line no-console
          console.debug('Previous request canceled')
        } else {
          // eslint-disable-next-line no-console
          console.error('Item fetch failed', e)
        }
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleSubmit = (event: any) => {
    event.preventDefault()
    fetchItems(itemSearchValue ? itemSearchValue : '')
  }

  const changeValue = (newSearch: string) => {
    source.cancel('Cancel previous search')
    source = axios.CancelToken.source()
    newSearch = newSearch.trim()
    setSearchValue(newSearch)
    if (newSearch === '') {
      setItems('', [])
    }
  }

  const clearSearch = () => {
    setSearchValue('')
    setItems('', [])
  }

  // Create the end element conditionally
  const renderEndElement = () => {
    if (!itemSearchValue) {
      return undefined
    }

    return (
      <Icon
        svg={Close}
        color="primary"
        size={{ s: 4, m: 6, l: 7 }}
        attributes={{
          style: {
            marginLeft: 'var(--st-unit-3)',
            cursor: 'pointer'
          },
          onClick: clearSearch,
          'data-testid': 'clear-search-btn-icon'
        }}
      />
    )
  }

  return (
    <View
      backgroundColor="primary"
      padding={[8, 5]}
      attributes={{ style: { paddingBottom: '20px' } }}
      direction="row"
      justify="start"
      align="start"
    >
      <View.Item className={style.SearchFullSize}>
        <Text
          fontFamily="body"
          weight="regular"
          color="50"
          attributes={{ style: { margin: '5px 0', fontSize: '24px' } }}
        >
          {t('part-search')}
        </Text>
      </View.Item>
      <View.Item className={style.SearchFullSize}>
        <form onSubmit={handleSubmit}>
          <TextField
            id="search-part-number"
            className={style.itemSearchBar}
            name="sku_part_number"
            label={''}
            inputAttributes={{ placeholder: t('search-sku-number'), autoFocus: true }}
            value={itemSearchValue}
            size="sm"
            onValueChange={changeValue}
            startElement={
              <Icon
                svg={Search}
                color="500"
                attributes={{
                  style: {
                    marginRight: 'var(--st-unit-3)',
                    cursor: 'pointer'
                  },
                  onClick: () => fetchItems(itemSearchValue),
                  'data-testid': 'search-icon-btn'
                }}
              />
            }
            endElement={renderEndElement()}
          />
        </form>
      </View.Item>
      {isLoading && (
        <View.Item className={style.SearchFullSize} attributes={{ style: { marginTop: '10px' } }}>
          <Loader size="large" variant="spinner" />
        </View.Item>
      )}
    </View>
  )
}

export default ItemSearch
